import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Truck, Package, ExternalLink, MapPin } from 'lucide-react'
import { cn, formatRelativeTime } from '@/lib/utils'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: number
  recordLinks?: Array<{
    type: string
    id: string
    label: string
    url: string
  }>
}

interface MessageListProps {
  messages: Message[]
}

export function MessageList({ messages }: MessageListProps) {
  // CRITICAL: Validate messages array (zero-defect enforcement)
  if (!Array.isArray(messages)) {
    throw new Error('MessageList: messages must be an array')
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  return (
    <div className="space-y-4">
      {messages.length === 0 && (
        <div className="text-center text-muted-foreground text-sm py-8">
          <p>Start a conversation with the AI Assistant.</p>
        </div>
      )}

      {messages.map((message, index) => {
        const isUser = message.role === 'user'

        return (
          <div
            key={index}
            className={cn(
              'flex w-full',
              isUser ? 'justify-end' : 'justify-start'
            )}
          >
            <div
              className={cn(
                'rounded-lg p-4 max-w-[85%] break-words',
                isUser
                  ? 'bg-primary text-primary-foreground ml-8'
                  : 'bg-muted text-foreground mr-8'
              )}
            >
              <p className="text-sm whitespace-pre-wrap leading-relaxed break-words overflow-wrap-anywhere">
                {message.content}
              </p>

              {message.recordLinks && message.recordLinks.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                  {message.recordLinks.map((link, idx) => (
                    <Link
                      key={idx}
                      to={link.url}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-border bg-background/50 hover:bg-accent hover:border-accent-foreground/20 transition-colors"
                    >
                      {link.type === 'vehicle' && (
                        <Truck className="h-4 w-4 text-blue-600" />
                      )}
                      {link.type === 'job' && (
                        <Package className="h-4 w-4 text-green-600" />
                      )}
                      {link.type === 'location' && (
                        <MapPin className="h-4 w-4 text-orange-600" />
                      )}
                      <span className="flex-1 font-semibold">{link.label}</span>
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                  ))}
                </div>
              )}

              <div className="text-sm text-muted-foreground mt-2">
                {formatRelativeTime(message.timestamp)}
              </div>
            </div>
          </div>
        )
      })}

      <div ref={messagesEndRef} />
    </div>
  )
}
